import {
  ROLE_ADMIN,
  ROLE_MANAGER,
  ROLE_BROKER_PARTNER,
  ROLE_BROKER,
} from 'variables/constants';

/**
 * Card Permission Configuration
 * 
 * Define permissions and feature flags for each card/component here.
 * This centralizes all permission logic and makes it easy to:
 * - See all card permissions in one place
 * - Change permissions without touching component code
 * - Support different views for different roles
 * 
 * Structure:
 * - cardId: Unique identifier for the card
 * - permissions: Array of role constants that can view this card
 * - featureFlagKey: Optional feature flag that must be enabled
 */
export interface CardPermissionConfig {
  cardId: string;
  permissions: number[];
  featureFlagKey?: string;
}

/**
 * Centralized card permission configuration
 * Add new cards here with their permissions and feature flags
 */
const CARD_PERMISSIONS: CardPermissionConfig[] = [
  {
    cardId: 'sales-dashboard',
    permissions: [ROLE_ADMIN, ROLE_MANAGER, ROLE_BROKER_PARTNER, ROLE_BROKER],
    featureFlagKey: 'enable-sales-contest',
  },
  {
    cardId: 'homepage-kpi-bar',
    permissions: [ROLE_ADMIN, ROLE_MANAGER, ROLE_BROKER_PARTNER, ROLE_BROKER],
  },
  {
    cardId: 'homepage-metrics',
    permissions: [ROLE_ADMIN, ROLE_MANAGER, ROLE_BROKER_PARTNER, ROLE_BROKER],
  },
];

/**
 * Get permission config for a specific card
 * @param cardId - The card identifier
 * @returns CardPermissionConfig or undefined if not found
 */
export const getCardConfig = (cardId: string): CardPermissionConfig | undefined => {
  return CARD_PERMISSIONS.find((config) => config.cardId === cardId);
};

/**
 * Get all card configs for a specific role
 * Useful for building role-specific views
 * @param role - The user role
 * @returns Array of card configs the role can access
 */
export const getCardsForRole = (role: number): CardPermissionConfig[] => {
  return CARD_PERMISSIONS.filter((config) => config.permissions.includes(role));
};

/**
 * Get all card IDs for a specific role
 * @param role - The user role
 * @returns Array of card IDs the role can access
 */
export const getCardIdsForRole = (role: number): string[] => {
  return getCardsForRole(role).map((config) => config.cardId);
};

/**
 * Check if a role can access a specific card
 * @param cardId - The card identifier
 * @param role - The user role
 * @returns boolean indicating if the role can access the card
 */
export const canRoleAccessCard = (cardId: string, role: number): boolean => {
  const config = getCardConfig(cardId);
  if (!config) return false;
  return config.permissions.includes(role);
};